import React from 'react';
import PageHeader from '../components/layout/PageHeader';
import Accordion, { AccordionItem } from '../components/ui/Accordion';
import { BookOpenIcon, ClipboardCheckIcon, UsersIcon, CalendarIcon, AwardIcon, BeakerIcon, PenToolIcon, LanguagesIcon, CalculatorIcon, GlobeIcon, MusicIcon, ActivityIcon } from 'lucide-react';
import Button from '../components/ui/Button';
const Academics = () => {
  return <div className="min-h-screen bg-white" data-id="element-937">
      <PageHeader title="Academics" description="Discover our comprehensive curriculum, dedicated teaching staff, and approach to academic excellence at Isonyin Grammar School." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/xwrtKazFgq7H6urQHRmWav/WhatsApp_Image_2025-10-24_at_10.01.12_%281%29.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Academics',
      path: '/academics'
    }]} data-id="element-938" />
      <div className="container mx-auto px-4 py-12" data-id="element-939">
        {/* Introduction Section */}
        <section className="mb-16" data-id="element-940">
          <div className="text-center mb-10" data-id="element-941">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-942">
              Academic Excellence
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-943"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-944">
              At Isonyin Grammar School, we are committed to providing a
              rigorous and comprehensive education that prepares students for
              future success.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-945">
            <div data-id="element-946">
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-947">
                Our academic program is designed to challenge students
                intellectually while fostering critical thinking, creativity,
                and a love for learning. We believe in a holistic approach to
                education that develops not only academic knowledge but also
                character, leadership skills, and social responsibility.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-948">
                With a curriculum that meets and exceeds national standards, our
                students are well-prepared for higher education and future
                careers. Our experienced teachers employ diverse teaching
                methods to accommodate different learning styles and ensure that
                each student reaches their full potential.
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-949">
                Beyond the classroom, we offer numerous opportunities for
                academic enrichment through clubs, competitions, and special
                programs that allow students to explore their interests and
                develop specialized skills.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-950">
              <div className="bg-blue-50 p-6 rounded-lg hover:shadow-md transition-shadow" data-id="element-951">
                <div className="flex items-center mb-3" data-id="element-952">
                  <BookOpenIcon className="h-6 w-6 text-blue-700 mr-2" data-id="element-953" />
                  <h3 className="font-bold text-blue-900" data-id="element-954">
                    Comprehensive Curriculum
                  </h3>
                </div>
                <p className="text-gray-700 text-sm" data-id="element-955">
                  Balanced academic program covering core subjects and electives
                </p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg hover:shadow-md transition-shadow" data-id="element-956">
                <div className="flex items-center mb-3" data-id="element-957">
                  <ClipboardCheckIcon className="h-6 w-6 text-blue-700 mr-2" data-id="element-958" />
                  <h3 className="font-bold text-blue-900" data-id="element-959">
                    Rigorous Assessment
                  </h3>
                </div>
                <p className="text-gray-700 text-sm" data-id="element-960">
                  Regular evaluation to track progress and identify areas for
                  growth
                </p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg hover:shadow-md transition-shadow" data-id="element-961">
                <div className="flex items-center mb-3" data-id="element-962">
                  <UsersIcon className="h-6 w-6 text-blue-700 mr-2" data-id="element-963" />
                  <h3 className="font-bold text-blue-900" data-id="element-964">
                    Qualified Teachers
                  </h3>
                </div>
                <p className="text-gray-700 text-sm" data-id="element-965">
                  Experienced educators dedicated to student success
                </p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg hover:shadow-md transition-shadow" data-id="element-966">
                <div className="flex items-center mb-3" data-id="element-967">
                  <AwardIcon className="h-6 w-6 text-blue-700 mr-2" data-id="element-968" />
                  <h3 className="font-bold text-blue-900" data-id="element-969">Proven Results</h3>
                </div>
                <p className="text-gray-700 text-sm" data-id="element-970">
                  Consistently high performance in national examinations
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Curriculum Section */}
        <section className="mb-16" id="curriculum" data-id="element-971">
          <div className="text-center mb-10" data-id="element-972">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-973">
              Our Curriculum
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-974"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-975">
              Our comprehensive curriculum is designed to provide a strong
              foundation in core subjects while offering opportunities for
              specialization and exploration.
            </p>
          </div>
          <div className="mb-12" data-id="element-976">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-977">
              <div className="rounded-lg overflow-hidden shadow-lg" data-id="element-978">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3uhcY5Mx6ch6FMKTi9n6s3/WhatsApp_Image_2025-10-24_at_10.01.12.jpg" alt="School assembly hall with student desks" className="w-full h-auto" data-id="element-979" />
                <div className="p-4 bg-white" data-id="element-980">
                  <h3 className="font-bold text-blue-900 mb-2" data-id="element-981">
                    Spacious Learning Spaces
                  </h3>
                  <p className="text-gray-700 text-sm" data-id="element-982">
                    Our large assembly hall doubles as an examination center,
                    accommodating hundreds of students comfortably
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg" data-id="element-983">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ez9ThFy3DkQ6XwMpYtSRNQ/WhatsApp_Image_2025-10-24_at_10.01.16.jpg" alt="Modern science laboratory with students" className="w-full h-auto" data-id="element-984" />
                <div className="p-4 bg-white" data-id="element-985">
                  <h3 className="font-bold text-blue-900 mb-2" data-id="element-986">
                    State-of-the-Art Laboratories
                  </h3>
                  <p className="text-gray-700 text-sm" data-id="element-987">
                    Modern science laboratories equipped with the latest
                    equipment for hands-on learning
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-988">
            <div className="lg:col-span-2" data-id="element-989">
              <h3 className="text-2xl font-bold text-blue-900 mb-6" data-id="element-990">
                Academic Structure
              </h3>
              <Accordion className="mb-8" variant="bordered" data-id="element-991">
                <AccordionItem title="Junior Secondary School (JSS 1-3)" defaultOpen data-id="element-992">
                  <p className="text-gray-700 mb-4" data-id="element-993">
                    The Junior Secondary curriculum provides a broad foundation
                    across various subject areas. Students study:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-994">
                    <div className="flex items-start" data-id="element-995">
                      <LanguagesIcon className="h-5 w-5 text-blue-700 mr-2 mt-0.5" data-id="element-996" />
                      <div data-id="element-997">
                        <p className="font-bold" data-id="element-998">Languages</p>
                        <p className="text-sm text-gray-600" data-id="element-999">
                          English, Yoruba, French
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1000">
                      <CalculatorIcon className="h-5 w-5 text-blue-700 mr-2 mt-0.5" data-id="element-1001" />
                      <div data-id="element-1002">
                        <p className="font-bold" data-id="element-1003">Mathematics</p>
                        <p className="text-sm text-gray-600" data-id="element-1004">
                          Basic Mathematics
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1005">
                      <BeakerIcon className="h-5 w-5 text-blue-700 mr-2 mt-0.5" data-id="element-1006" />
                      <div data-id="element-1007">
                        <p className="font-bold" data-id="element-1008">Sciences</p>
                        <p className="text-sm text-gray-600" data-id="element-1009">
                          Integrated Science, Agricultural Science, Basic
                          Technology
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1010">
                      <GlobeIcon className="h-5 w-5 text-blue-700 mr-2 mt-0.5" data-id="element-1011" />
                      <div data-id="element-1012">
                        <p className="font-bold" data-id="element-1013">Social Studies</p>
                        <p className="text-sm text-gray-600" data-id="element-1014">
                          Social Studies, Civic Education, Religious Studies
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1015">
                      <PenToolIcon className="h-5 w-5 text-blue-700 mr-2 mt-0.5" data-id="element-1016" />
                      <div data-id="element-1017">
                        <p className="font-bold" data-id="element-1018">Creative Arts</p>
                        <p className="text-sm text-gray-600" data-id="element-1019">
                          Fine Arts, Music, Home Economics
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1020">
                      <ActivityIcon className="h-5 w-5 text-blue-700 mr-2 mt-0.5" data-id="element-1021" />
                      <div data-id="element-1022">
                        <p className="font-bold" data-id="element-1023">Physical Education</p>
                        <p className="text-sm text-gray-600" data-id="element-1024">
                          Physical & Health Education
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700" data-id="element-1025">
                    At the end of JSS 3, students take the Junior School
                    Certificate Examination (JSCE) which qualifies them for
                    Senior Secondary education.
                  </p>
                </AccordionItem>
                <AccordionItem title="Senior Secondary School (SSS 1-3)" data-id="element-1026">
                  <p className="text-gray-700 mb-4" data-id="element-1027">
                    In Senior Secondary, students begin to specialize while
                    maintaining core subjects. They choose one of three tracks:
                  </p>
                  <div className="space-y-6 mb-4" data-id="element-1028">
                    <div className="bg-blue-50 p-4 rounded-lg" data-id="element-1029">
                      <h4 className="font-bold text-blue-900 mb-2" data-id="element-1030">
                        Science Track
                      </h4>
                      <p className="text-gray-700 mb-2" data-id="element-1031">Core subjects:</p>
                      <ul className="grid grid-cols-2 gap-2 text-sm text-gray-700" data-id="element-1032">
                        <li className="flex items-center" data-id="element-1033">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1034"></div>
                          <span data-id="element-1035">English Language</span>
                        </li>
                        <li className="flex items-center" data-id="element-1036">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1037"></div>
                          <span data-id="element-1038">Mathematics</span>
                        </li>
                        <li className="flex items-center" data-id="element-1039">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1040"></div>
                          <span data-id="element-1041">Physics</span>
                        </li>
                        <li className="flex items-center" data-id="element-1042">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1043"></div>
                          <span data-id="element-1044">Chemistry</span>
                        </li>
                        <li className="flex items-center" data-id="element-1045">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1046"></div>
                          <span data-id="element-1047">Biology</span>
                        </li>
                        <li className="flex items-center" data-id="element-1048">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1049"></div>
                          <span data-id="element-1050">Civic Education</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg" data-id="element-1051">
                      <h4 className="font-bold text-blue-900 mb-2" data-id="element-1052">
                        Commercial Track
                      </h4>
                      <p className="text-gray-700 mb-2" data-id="element-1053">Core subjects:</p>
                      <ul className="grid grid-cols-2 gap-2 text-sm text-gray-700" data-id="element-1054">
                        <li className="flex items-center" data-id="element-1055">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1056"></div>
                          <span data-id="element-1057">English Language</span>
                        </li>
                        <li className="flex items-center" data-id="element-1058">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1059"></div>
                          <span data-id="element-1060">Mathematics</span>
                        </li>
                        <li className="flex items-center" data-id="element-1061">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1062"></div>
                          <span data-id="element-1063">Economics</span>
                        </li>
                        <li className="flex items-center" data-id="element-1064">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1065"></div>
                          <span data-id="element-1066">Commerce</span>
                        </li>
                        <li className="flex items-center" data-id="element-1067">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1068"></div>
                          <span data-id="element-1069">Accounting</span>
                        </li>
                        <li className="flex items-center" data-id="element-1070">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1071"></div>
                          <span data-id="element-1072">Civic Education</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg" data-id="element-1073">
                      <h4 className="font-bold text-blue-900 mb-2" data-id="element-1074">
                        Arts Track
                      </h4>
                      <p className="text-gray-700 mb-2" data-id="element-1075">Core subjects:</p>
                      <ul className="grid grid-cols-2 gap-2 text-sm text-gray-700" data-id="element-1076">
                        <li className="flex items-center" data-id="element-1077">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1078"></div>
                          <span data-id="element-1079">English Language</span>
                        </li>
                        <li className="flex items-center" data-id="element-1080">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1081"></div>
                          <span data-id="element-1082">Mathematics</span>
                        </li>
                        <li className="flex items-center" data-id="element-1083">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1084"></div>
                          <span data-id="element-1085">Literature in English</span>
                        </li>
                        <li className="flex items-center" data-id="element-1086">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1087"></div>
                          <span data-id="element-1088">Government</span>
                        </li>
                        <li className="flex items-center" data-id="element-1089">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1090"></div>
                          <span data-id="element-1091">History</span>
                        </li>
                        <li className="flex items-center" data-id="element-1092">
                          <div className="h-1.5 w-1.5 bg-blue-700 rounded-full mr-2" data-id="element-1093"></div>
                          <span data-id="element-1094">Civic Education</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-gray-700" data-id="element-1095">
                    Students also select additional elective subjects based on
                    their track and interests. At the end of SSS 3, students
                    take the West African Senior School Certificate Examination
                    (WASSCE) and the National Examination Council (NECO)
                    examinations.
                  </p>
                </AccordionItem>
              </Accordion>
              <div className="bg-blue-900 text-white p-6 rounded-lg mb-6" data-id="element-1096">
                <h3 className="text-xl font-bold mb-4" data-id="element-1097">Teaching Methodology</h3>
                <p className="mb-4" data-id="element-1098">
                  Our teaching approach combines traditional and modern
                  methodologies to ensure effective learning:
                </p>
                <ul className="space-y-3" data-id="element-1099">
                  <li className="flex items-start" data-id="element-1100">
                    <div className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1101">✓</div>
                    <p data-id="element-1102">
                      Interactive classroom discussions that encourage critical
                      thinking
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1103">
                    <div className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1104">✓</div>
                    <p data-id="element-1105">
                      Practical laboratory sessions for hands-on learning in
                      sciences
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1106">
                    <div className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1107">✓</div>
                    <p data-id="element-1108">
                      Project-based learning that develops research and
                      collaboration skills
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1109">
                    <div className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1110">✓</div>
                    <p data-id="element-1111">
                      Technology integration through our computer labs and
                      digital resources
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1112">
                    <div className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1113">✓</div>
                    <p data-id="element-1114">
                      Field trips and guest speakers to connect classroom
                      learning to real-world applications
                    </p>
                  </li>
                </ul>
              </div>
              <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-100" data-id="element-1115">
                <div className="flex items-center mb-4" data-id="element-1116">
                  <BookOpenIcon className="h-6 w-6 text-yellow-600 mr-3" data-id="element-1117" />
                  <h3 className="text-xl font-bold text-blue-900" data-id="element-1118">
                    Special Academic Programs
                  </h3>
                </div>
                <div className="space-y-4" data-id="element-1119">
                  <div data-id="element-1120">
                    <h4 className="font-bold text-blue-900" data-id="element-1121">
                      Advanced Mathematics Program
                    </h4>
                    <p className="text-gray-700 text-sm" data-id="element-1122">
                      Special coaching for mathematically gifted students,
                      preparing them for national and international
                      competitions.
                    </p>
                  </div>
                  <div data-id="element-1123">
                    <h4 className="font-bold text-blue-900" data-id="element-1124">
                      Language Immersion
                    </h4>
                    <p className="text-gray-700 text-sm" data-id="element-1125">
                      Enhanced language learning opportunities in French and
                      Yoruba through cultural activities and exchange programs.
                    </p>
                  </div>
                  <div data-id="element-1126">
                    <h4 className="font-bold text-blue-900" data-id="element-1127">
                      Science Innovation Hub
                    </h4>
                    <p className="text-gray-700 text-sm" data-id="element-1128">
                      An after-school program where students work on science
                      projects and innovations under the guidance of experienced
                      mentors.
                    </p>
                  </div>
                  <div data-id="element-1129">
                    <h4 className="font-bold text-blue-900" data-id="element-1130">
                      Leadership Development
                    </h4>
                    <p className="text-gray-700 text-sm" data-id="element-1131">
                      A structured program to develop leadership skills through
                      workshops, community service, and practical leadership
                      roles.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-1132">
              <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-1133">
                <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-1134">
                  Academic Support
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1135">
                  We are committed to helping every student succeed
                  academically. Our support services include:
                </p>
                <ul className="space-y-3 text-gray-700" data-id="element-1136">
                  <li className="flex items-start" data-id="element-1137">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1138">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1139">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1140" />
                      </svg>
                    </div>
                    <p data-id="element-1141">
                      Tutorial sessions for students needing additional help
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1142">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1143">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1144">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1145" />
                      </svg>
                    </div>
                    <p data-id="element-1146">
                      Remedial classes for students who need to strengthen
                      specific skills
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1147">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1148">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1149">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1150" />
                      </svg>
                    </div>
                    <p data-id="element-1151">
                      Guidance counseling for academic planning and career
                      guidance
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1152">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1153">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1154">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1155" />
                      </svg>
                    </div>
                    <p data-id="element-1156">
                      Study skills workshops to improve learning effectiveness
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-1157">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1158">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1159">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1160" />
                      </svg>
                    </div>
                    <p data-id="element-1161">
                      Peer tutoring program where high-achieving students help
                      their peers
                    </p>
                  </li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-1162">
                <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-1163">
                  Assessment & Reporting
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-1164">
                  Student progress is regularly assessed through:
                </p>
                <ul className="space-y-3 text-gray-700" data-id="element-1165">
                  <li className="flex items-start" data-id="element-1166">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1167">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1168">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1169" />
                      </svg>
                    </div>
                    <p data-id="element-1170">Continuous Assessment Tests (CATs)</p>
                  </li>
                  <li className="flex items-start" data-id="element-1171">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1172">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1173">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1174" />
                      </svg>
                    </div>
                    <p data-id="element-1175">Mid-term examinations</p>
                  </li>
                  <li className="flex items-start" data-id="element-1176">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1177">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1178">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1179" />
                      </svg>
                    </div>
                    <p data-id="element-1180">End-of-term examinations</p>
                  </li>
                  <li className="flex items-start" data-id="element-1181">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1182">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1183">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1184" />
                      </svg>
                    </div>
                    <p data-id="element-1185">Project work and assignments</p>
                  </li>
                  <li className="flex items-start" data-id="element-1186">
                    <div className="h-5 w-5 text-blue-700 mr-2" data-id="element-1187">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-1188">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-1189" />
                      </svg>
                    </div>
                    <p data-id="element-1190">Class participation and practical work</p>
                  </li>
                </ul>
                <p className="mt-4 text-gray-700" data-id="element-1191">
                  Comprehensive reports are issued at the end of each term, and
                  parent-teacher meetings are held to discuss student progress.
                </p>
              </div>
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-6 rounded-lg" data-id="element-1192">
                <h3 className="text-xl font-bold mb-4" data-id="element-1193">Academic Facilities</h3>
                <ul className="space-y-3" data-id="element-1194">
                  <li className="flex items-start" data-id="element-1195">
                    <BeakerIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1196" />
                    <p data-id="element-1197">Well-equipped science laboratories</p>
                  </li>
                  <li className="flex items-start" data-id="element-1198">
                    <BookOpenIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1199" />
                    <p data-id="element-1200">Modern library with extensive resources</p>
                  </li>
                  <li className="flex items-start" data-id="element-1201">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1202">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-1203" />
                    </svg>
                    <p data-id="element-1204">Computer labs with internet access</p>
                  </li>
                  <li className="flex items-start" data-id="element-1205">
                    <MusicIcon className="h-5 w-5 text-yellow-400 mr-2" data-id="element-1206" />
                    <p data-id="element-1207">Music and art studios</p>
                  </li>
                  <li className="flex items-start" data-id="element-1208">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1209">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" data-id="element-1210" />
                    </svg>
                    <p data-id="element-1211">Dedicated classrooms for each subject</p>
                  </li>
                </ul>
                <div className="mt-6" data-id="element-1212">
                  <Button href="/campus" variant="primary" size="sm" data-id="element-1213">
                    View Campus Facilities
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Science Laboratories Section */}
        <section className="mb-16 bg-gray-50 py-12" data-id="element-1214">
          <div className="container mx-auto px-4" data-id="element-1215">
            <div className="text-center mb-10" data-id="element-1216">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1217">
                Science Laboratories
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1218"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1219">
                Our well-equipped science laboratories provide students with
                hands-on experience in scientific inquiry and experimentation
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12" data-id="element-1220">
              <div data-id="element-1221">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hWx5tyEVbwQVrxum4dtVj3/WhatsApp_Image_2025-10-24_at_13.20.17_%281%29.jpg" alt="Students in science laboratory" className="w-full rounded-lg shadow-lg" data-id="element-1222" />
              </div>
              <div data-id="element-1223">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1224">
                  Modern Laboratory Facilities
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1225">
                  Our science laboratories are equipped with modern apparatus
                  and equipment that enable students to conduct experiments
                  safely and effectively. From physics to chemistry to biology,
                  students gain practical experience that reinforces theoretical
                  concepts learned in the classroom.
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1226">
                  Under the supervision of qualified science teachers, students
                  develop critical thinking skills, learn the scientific method,
                  and cultivate a passion for scientific discovery. Our
                  laboratories feature proper safety equipment, adequate
                  workspace, and well-organized storage for chemicals and
                  apparatus.
                </p>
                <ul className="space-y-2" data-id="element-1227">
                  <li className="flex items-start" data-id="element-1228">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-1" data-id="element-1229">
                      <svg className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1230">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1231" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1232">
                      Physics laboratory with modern equipment
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1233">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-1" data-id="element-1234">
                      <svg className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1235">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1236" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1237">
                      Chemistry laboratory with safety features
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1238">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-1" data-id="element-1239">
                      <svg className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1240">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1241" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1242">
                      Biology laboratory with microscopes and specimens
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1243">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-1" data-id="element-1244">
                      <svg className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1245">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1246" />
                      </svg>
                    </div>
                    <span className="text-gray-700" data-id="element-1247">
                      Qualified science teachers and lab technicians
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        {/* Teaching Excellence Section */}
        <section className="mb-16 bg-gray-50 py-12" data-id="element-1248">
          <div className="container mx-auto px-4" data-id="element-1249">
            <div className="text-center mb-10" data-id="element-1250">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1251">
                Teaching Excellence
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1252"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1253">
                Our dedicated teachers employ modern pedagogical methods to
                ensure effective learning
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12" data-id="element-1254">
              <div data-id="element-1255">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rYxE1D1a3w1zMFiLJm2aU1/WhatsApp_Image_2025-10-24_at_10.01.17.jpg" alt="Teacher conducting chemistry lesson" className="w-full rounded-lg shadow-lg" data-id="element-1256" />
              </div>
              <div data-id="element-1257">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1258">
                  Interactive Teaching Methods
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1259">
                  Our teachers use interactive whiteboards and modern teaching
                  aids to make lessons engaging and effective. We employ proven
                  pedagogical methods like PSI/PMI to ensure students grasp
                  concepts thoroughly.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-1260">
                  Every classroom is equipped with teaching materials that
                  support visual, auditory, and kinesthetic learning styles,
                  ensuring that every student can learn effectively.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12" data-id="element-1261">
              <div className="order-2 md:order-1" data-id="element-1262">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1263">
                  Technology-Enhanced Learning
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1264">
                  Our teachers integrate technology into their lessons using
                  laptops, projectors, and interactive displays. This modern
                  approach helps students visualize complex concepts in physics,
                  mathematics, and other subjects.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-1265">
                  From wave mechanics to mathematical functions, our teachers
                  use digital tools to make abstract concepts concrete and
                  understandable for students.
                </p>
              </div>
              <div className="order-1 md:order-2" data-id="element-1266">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/26CHK6sEK66nfVUNdvbwf4/WhatsApp_Image_2025-10-24_at_10.01.20_%282%29.jpg" alt="Teacher using technology in physics lesson" className="w-full rounded-lg shadow-lg" data-id="element-1267" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12" data-id="element-1268">
              <div data-id="element-1269">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fBcdZe3j5kT56auYSAwRFV/WhatsApp_Image_2025-10-24_at_10.01.23_%281%29.jpg" alt="Female teacher teaching mathematics" className="w-full rounded-lg shadow-lg" data-id="element-1270" />
              </div>
              <div data-id="element-1271">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1272">
                  Mathematics Excellence
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1273">
                  Our mathematics teachers are experts in their field, teaching
                  advanced topics like differential calculus, circle geometry,
                  and music-mathematics therapy. They use innovative approaches
                  to make mathematics accessible and interesting.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-1274">
                  Students benefit from clear explanations, step-by-step
                  problem-solving techniques, and real-world applications of
                  mathematical concepts.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-1275">
              <div className="order-2 md:order-1" data-id="element-1276">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1277">
                  Biology & Life Sciences
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1278">
                  Our biology teachers bring concepts to life using anatomical
                  models, skeletons, and other teaching aids. Students engage in
                  hands-on learning that makes complex biological concepts easy
                  to understand.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-1279">
                  From human anatomy to supporting tissues in animals, our
                  comprehensive approach ensures students develop a deep
                  understanding of life sciences.
                </p>
              </div>
              <div className="order-1 md:order-2" data-id="element-1280">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kMX4mMRKhHUYMbFhqjEMu9/WhatsApp_Image_2025-10-24_at_10.01.20_%281%29.jpg" alt="Teacher teaching biology with anatomical models" className="w-full rounded-lg shadow-lg" data-id="element-1281" />
              </div>
            </div>
          </div>
        </section>
        {/* Technology Integration Section - Add ICT Room */}
        <section className="mb-16" data-id="element-1282">
          <div className="container mx-auto px-4" data-id="element-1283">
            <div className="text-center mb-10" data-id="element-1284">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1285">
                Technology in Education
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1286"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1287">
                We integrate technology into our curriculum to prepare students
                for the digital age
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1288">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-1289">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3GFAKQjjuFe5vR4bv8XoiJ/WhatsApp_Image_2025-10-24_at_10.01.21_%281%29.jpg" alt="Students in computer lab" className="w-full h-64 object-cover" data-id="element-1290" />
                <div className="p-6" data-id="element-1291">
                  <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-1292">
                    Computer Laboratory
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-1293">
                    Our modern computer lab is equipped with up-to-date
                    computers where students learn essential digital skills,
                    programming, and computer applications under expert
                    guidance.
                  </p>
                  <p className="text-sm text-gray-600 italic" data-id="element-1294">
                    Preparing students for the digital economy with hands-on
                    technology training
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden" data-id="element-1295">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tj13uTG1CCX8nkvziVAAab/WhatsApp_Image_2025-10-24_at_10.01.17_%281%29.jpg" alt="ICT Room donated by Old Students Association" className="w-full h-64 object-cover" data-id="element-1296" />
                <div className="p-6" data-id="element-1297">
                  <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-1298">
                    ICT Laboratory
                  </h3>
                  <p className="text-gray-700 mb-4" data-id="element-1299">
                    Our Information and Communication Technology (ICT) room,
                    generously donated by the Old Students Association 75/80
                    Set, is equipped with modern computers and internet
                    connectivity.
                  </p>
                  <p className="text-sm text-gray-600 italic" data-id="element-1300">
                    A testament to our alumni's commitment to advancing
                    education at IGS
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-8 rounded-lg" data-id="element-1301">
              <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1302">
                Digital Literacy Programs
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1303">
                <div data-id="element-1304">
                  <h4 className="font-bold text-blue-900 mb-2" data-id="element-1305">
                    Basic Computing
                  </h4>
                  <p className="text-gray-700 text-sm" data-id="element-1306">
                    Students learn word processing, spreadsheets, presentations,
                    and internet research skills.
                  </p>
                </div>
                <div data-id="element-1307">
                  <h4 className="font-bold text-blue-900 mb-2" data-id="element-1308">Programming</h4>
                  <p className="text-gray-700 text-sm" data-id="element-1309">
                    Introduction to coding and programming languages, preparing
                    students for careers in technology.
                  </p>
                </div>
                <div data-id="element-1310">
                  <h4 className="font-bold text-blue-900 mb-2" data-id="element-1311">
                    Digital Citizenship
                  </h4>
                  <p className="text-gray-700 text-sm" data-id="element-1312">
                    Teaching responsible and ethical use of technology, online
                    safety, and digital etiquette.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Library Section */}
        <section className="mb-16 bg-gray-50 py-12" data-id="element-1313">
          <div className="container mx-auto px-4" data-id="element-1314">
            <div className="text-center mb-10" data-id="element-1315">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1316">
                School Library
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1317"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1318">
                Our well-stocked library provides students with access to a wide
                range of educational resources
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-1319">
              <div data-id="element-1320">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tDi3zMHQVzwj3EoB8pBjyc/WhatsApp_Image_2025-10-24_at_10.01.18_%282%29.jpg" alt="School library with bookshelves" className="w-full rounded-lg shadow-lg" data-id="element-1321" />
              </div>
              <div data-id="element-1322">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1323">
                  Comprehensive Learning Resources
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1324">
                  Our library houses thousands of books across all subject
                  areas, from mathematics and sciences to literature and social
                  studies. Students have access to reference materials,
                  textbooks, and recreational reading materials.
                </p>
                <ul className="space-y-3 text-gray-700" data-id="element-1325">
                  <li className="flex items-start" data-id="element-1326">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1327">
                      <span className="text-blue-700 text-sm" data-id="element-1328">✓</span>
                    </div>
                    <span data-id="element-1329">Organized by subject areas for easy access</span>
                  </li>
                  <li className="flex items-start" data-id="element-1330">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1331">
                      <span className="text-blue-700 text-sm" data-id="element-1332">✓</span>
                    </div>
                    <span data-id="element-1333">Quiet study areas for individual learning</span>
                  </li>
                  <li className="flex items-start" data-id="element-1334">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1335">
                      <span className="text-blue-700 text-sm" data-id="element-1336">✓</span>
                    </div>
                    <span data-id="element-1337">Regularly updated with new titles</span>
                  </li>
                  <li className="flex items-start" data-id="element-1338">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2" data-id="element-1339">
                      <span className="text-blue-700 text-sm" data-id="element-1340">✓</span>
                    </div>
                    <span data-id="element-1341">Supervised by trained library staff</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        {/* Performance Records Section */}
        <section className="mb-16 bg-gray-50 py-12 rounded-lg" id="performance" data-id="element-1342">
          <div className="container mx-auto px-4" data-id="element-1343">
            <div className="text-center mb-10" data-id="element-1344">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1345">
                Academic Performance
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1346"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1347">
                Our students consistently achieve excellent results in national
                examinations and competitions.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-12" data-id="element-1348">
              <div data-id="element-1349">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1350">
                  Examination Results
                </h3>
                <p className="text-gray-700 mb-6" data-id="element-1351">
                  Isonyin Grammar School has maintained an outstanding record in
                  national examinations over the years. Our students
                  consistently perform above the national average in both the
                  West African Senior School Certificate Examination (WASSCE)
                  and the National Examination Council (NECO) examinations.
                </p>
                <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-1352">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1353">
                    WAEC Results (2022)
                  </h4>
                  <div className="overflow-x-auto" data-id="element-1354">
                    <table className="min-w-full bg-white" data-id="element-1355">
                      <thead data-id="element-1356">
                        <tr className="bg-blue-900 text-white" data-id="element-1357">
                          <th className="py-2 px-4 text-left" data-id="element-1358">Grade</th>
                          <th className="py-2 px-4 text-left" data-id="element-1359">
                            Percentage of Students
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-1360">
                        <tr data-id="element-1361">
                          <td className="py-2 px-4 font-medium" data-id="element-1362">
                            A1-B3 (Distinction)
                          </td>
                          <td className="py-2 px-4" data-id="element-1363">68%</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1364">
                          <td className="py-2 px-4 font-medium" data-id="element-1365">
                            C4-C6 (Credit)
                          </td>
                          <td className="py-2 px-4" data-id="element-1366">27%</td>
                        </tr>
                        <tr data-id="element-1367">
                          <td className="py-2 px-4 font-medium" data-id="element-1368">
                            D7-E8 (Pass)
                          </td>
                          <td className="py-2 px-4" data-id="element-1369">4%</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1370">
                          <td className="py-2 px-4 font-medium" data-id="element-1371">F9 (Fail)</td>
                          <td className="py-2 px-4" data-id="element-1372">1%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-1373">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1374">
                    5-Year Performance Trend
                  </h4>
                  <p className="text-gray-700 mb-3" data-id="element-1375">
                    Percentage of students with 5 credits and above (including
                    English and Mathematics):
                  </p>
                  <div className="relative h-60" data-id="element-1376">
                    <div className="absolute bottom-0 left-0 w-full h-full flex items-end" data-id="element-1377">
                      <div className="w-1/5 h-[75%] bg-blue-700 mx-1 relative group" data-id="element-1378">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1379">
                          75%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1380">
                          2018
                        </div>
                      </div>
                      <div className="w-1/5 h-[82%] bg-blue-700 mx-1 relative group" data-id="element-1381">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1382">
                          82%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1383">
                          2019
                        </div>
                      </div>
                      <div className="w-1/5 h-[88%] bg-blue-700 mx-1 relative group" data-id="element-1384">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1385">
                          88%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1386">
                          2020
                        </div>
                      </div>
                      <div className="w-1/5 h-[90%] bg-blue-700 mx-1 relative group" data-id="element-1387">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1388">
                          90%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1389">
                          2021
                        </div>
                      </div>
                      <div className="w-1/5 h-[95%] bg-blue-700 mx-1 relative group" data-id="element-1390">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1391">
                          95%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1392">
                          2022
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-1393">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1394">
                  Academic Achievements
                </h3>
                <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-1395">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1396">
                    National & International Competitions
                  </h4>
                  <ul className="space-y-4" data-id="element-1397">
                    <li className="flex items-start" data-id="element-1398">
                      <AwardIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-1399" />
                      <div data-id="element-1400">
                        <p className="font-bold" data-id="element-1401">
                          National Mathematics Competition
                        </p>
                        <p className="text-sm text-gray-700" data-id="element-1402">
                          1st Place (2022), 2nd Place (2021)
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1403">
                      <AwardIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-1404" />
                      <div data-id="element-1405">
                        <p className="font-bold" data-id="element-1406">National Science Olympiad</p>
                        <p className="text-sm text-gray-700" data-id="element-1407">
                          Top 5 Finalists (2022), Regional Champions (2021,
                          2020)
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1408">
                      <AwardIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-1409" />
                      <div data-id="element-1410">
                        <p className="font-bold" data-id="element-1411">Debate Competition</p>
                        <p className="text-sm text-gray-700" data-id="element-1412">
                          State Champions (2022), National Semi-finalists (2021)
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1413">
                      <AwardIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-1414" />
                      <div data-id="element-1415">
                        <p className="font-bold" data-id="element-1416">Robotics Challenge</p>
                        <p className="text-sm text-gray-700" data-id="element-1417">
                          2nd Place, National Level (2022)
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start" data-id="element-1418">
                      <AwardIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-1419" />
                      <div data-id="element-1420">
                        <p className="font-bold" data-id="element-1421">Essay Writing Contest</p>
                        <p className="text-sm text-gray-700" data-id="element-1422">
                          National Winner (2021), State Winner (2022)
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-1423">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1424">
                    University Admissions
                  </h4>
                  <p className="text-gray-700 mb-3" data-id="element-1425">
                    Our graduates consistently gain admission to top
                    universities in Nigeria and abroad:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700" data-id="element-1426">
                    <div className="flex items-center" data-id="element-1427">
                      <div className="h-2 w-2 bg-blue-700 rounded-full mr-2" data-id="element-1428"></div>
                      <span data-id="element-1429">University of Lagos</span>
                    </div>
                    <div className="flex items-center" data-id="element-1430">
                      <div className="h-2 w-2 bg-blue-700 rounded-full mr-2" data-id="element-1431"></div>
                      <span data-id="element-1432">University of Ibadan</span>
                    </div>
                    <div className="flex items-center" data-id="element-1433">
                      <div className="h-2 w-2 bg-blue-700 rounded-full mr-2" data-id="element-1434"></div>
                      <span data-id="element-1435">Covenant University</span>
                    </div>
                    <div className="flex items-center" data-id="element-1436">
                      <div className="h-2 w-2 bg-blue-700 rounded-full mr-2" data-id="element-1437"></div>
                      <span data-id="element-1438">Obafemi Awolowo University</span>
                    </div>
                    <div className="flex items-center" data-id="element-1439">
                      <div className="h-2 w-2 bg-blue-700 rounded-full mr-2" data-id="element-1440"></div>
                      <span data-id="element-1441">University of Benin</span>
                    </div>
                    <div className="flex items-center" data-id="element-1442">
                      <div className="h-2 w-2 bg-blue-700 rounded-full mr-2" data-id="element-1443"></div>
                      <span data-id="element-1444">University of Nigeria</span>
                    </div>
                  </div>
                  <p className="mt-3 text-gray-700" data-id="element-1445">
                    International universities our alumni attend include
                    institutions in the UK, US, Canada, and other countries.
                  </p>
                </div>
                <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-100" data-id="element-1446">
                  <div className="flex items-center mb-3" data-id="element-1447">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1448">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-1449" />
                    </svg>
                    <h4 className="font-bold text-blue-900" data-id="element-1450">Success Story</h4>
                  </div>
                  <p className="text-gray-700 italic mb-3" data-id="element-1451">
                    "Our 2022 valedictorian, Oluwaseun Adebayo, scored A1 in all
                    nine subjects in WAEC and was offered scholarships to three
                    international universities. He is currently studying
                    Mechanical Engineering at Imperial College London."
                  </p>
                  <p className="text-right text-sm text-gray-600" data-id="element-1452">
                    — Dr. Oluwasegun Adeyemi, Principal
                  </p>
                </div>
              </div>
            </div>
            <div className="text-center" data-id="element-1453">
              <Button href="/academics/results" variant="primary" className="mx-auto" data-id="element-1454">
                View Detailed Performance Records
              </Button>
            </div>
          </div>
        </section>
        {/* Teaching Staff Section */}
        <section className="py-16 bg-white" id="staff" data-id="element-1455">
          <div className="container mx-auto px-4" data-id="element-1456">
            <div className="text-center mb-10" data-id="element-1457">
              <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1458">
                Our Teaching Staff
              </h2>
              <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1459"></div>
              <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1460">
                Meet our dedicated educators who are committed to nurturing the
                next generation of leaders.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-12" data-id="element-1461">
              <div data-id="element-1462">
                <h3 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-1463">
                  Excellence in Teaching
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1464">
                  Our teaching staff comprises highly qualified and experienced
                  educators who are passionate about their subjects and
                  dedicated to student success. They employ innovative teaching
                  methods, including the PSI/PMI pedagogy, to ensure that every
                  student receives a world-class education.
                </p>
                <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1465">
                  Our teachers undergo continuous professional development to
                  stay current with the latest educational trends and best
                  practices. They work collaboratively to create a supportive
                  learning environment where students can thrive academically
                  and personally.
                </p>
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-100" data-id="element-1466">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1467">
                    Our Staff Qualifications
                  </h4>
                  <ul className="space-y-2 text-gray-700" data-id="element-1468">
                    <li className="flex items-start" data-id="element-1469">
                      <span className="text-yellow-500 mr-2" data-id="element-1470">•</span>
                      <span data-id="element-1471">
                        100% of teachers hold at least a Bachelor's degree in
                        their subject area
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1472">
                      <span className="text-yellow-500 mr-2" data-id="element-1473">•</span>
                      <span data-id="element-1474">
                        40% hold Master's degrees or higher qualifications
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1475">
                      <span className="text-yellow-500 mr-2" data-id="element-1476">•</span>
                      <span data-id="element-1477">
                        All teachers are registered with the Teachers
                        Registration Council of Nigeria (TRCN)
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1478">
                      <span className="text-yellow-500 mr-2" data-id="element-1479">•</span>
                      <span data-id="element-1480">
                        Regular training in modern pedagogical approaches
                        including PSI/PMI methodology
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div data-id="element-1481">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kCtjXZxCNmomG25TYhbjrQ/WhatsApp_Image_2025-11-07_at_10.21.13.jpg" alt="Junior School Staff in Session" className="w-full h-auto rounded-lg shadow-lg" data-id="element-1482" />
                <p className="text-center text-sm text-gray-600 mt-3" data-id="element-1483">
                  Our dedicated Junior School staff during a professional
                  development session
                </p>
              </div>
            </div>
            <div className="bg-blue-50 p-8 rounded-lg border border-blue-100 mb-8" data-id="element-1484">
              <div className="text-center mb-6" data-id="element-1485">
                <h3 className="text-2xl font-bold text-blue-900" data-id="element-1486">
                  Faculty Profile
                </h3>
                <p className="text-gray-700 mt-2" data-id="element-1487">
                  Our teaching staff consists of 45 full-time teachers with
                  diverse qualifications and experience.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1488">
                <div data-id="element-1489">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1490">
                    Qualifications
                  </h4>
                  <div className="relative h-60" data-id="element-1491">
                    <div className="absolute bottom-0 left-0 w-full h-full flex items-end" data-id="element-1492">
                      <div className="w-1/3 h-[35%] bg-blue-700 mx-1 relative group" data-id="element-1493">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1494">
                          35%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1495">
                          Bachelor's
                        </div>
                      </div>
                      <div className="w-1/3 h-[55%] bg-blue-700 mx-1 relative group" data-id="element-1496">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1497">
                          55%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1498">
                          Master's
                        </div>
                      </div>
                      <div className="w-1/3 h-[10%] bg-blue-700 mx-1 relative group" data-id="element-1499">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1500">
                          10%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1501">
                          PhD
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-1502">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1503">
                    Teaching Experience
                  </h4>
                  <div className="relative h-60" data-id="element-1504">
                    <div className="absolute bottom-0 left-0 w-full h-full flex items-end" data-id="element-1505">
                      <div className="w-1/4 h-[15%] bg-blue-700 mx-1 relative group" data-id="element-1506">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1507">
                          15%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1508">
                          0-5 yrs
                        </div>
                      </div>
                      <div className="w-1/4 h-[35%] bg-blue-700 mx-1 relative group" data-id="element-1509">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1510">
                          35%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1511">
                          6-10 yrs
                        </div>
                      </div>
                      <div className="w-1/4 h-[30%] bg-blue-700 mx-1 relative group" data-id="element-1512">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1513">
                          30%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1514">
                          11-15 yrs
                        </div>
                      </div>
                      <div className="w-1/4 h-[20%] bg-blue-700 mx-1 relative group" data-id="element-1515">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-blue-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1516">
                          20%
                        </div>
                        <div className="absolute bottom-0 left-0 w-full text-center text-xs font-medium py-1" data-id="element-1517">
                          16+ yrs
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-1518">
                  <h4 className="font-bold text-blue-900 mb-3" data-id="element-1519">
                    Subject Distribution
                  </h4>
                  <ul className="space-y-2" data-id="element-1520">
                    <li className="flex items-center" data-id="element-1521">
                      <div className="w-24 bg-gray-200 h-4 rounded-full mr-3" data-id="element-1522">
                        <div className="bg-blue-700 h-4 rounded-full" style={{
                        width: '25%'
                      }} data-id="element-1523"></div>
                      </div>
                      <span className="text-sm" data-id="element-1524">Languages (25%)</span>
                    </li>
                    <li className="flex items-center" data-id="element-1525">
                      <div className="w-24 bg-gray-200 h-4 rounded-full mr-3" data-id="element-1526">
                        <div className="bg-blue-700 h-4 rounded-full" style={{
                        width: '20%'
                      }} data-id="element-1527"></div>
                      </div>
                      <span className="text-sm" data-id="element-1528">Sciences (20%)</span>
                    </li>
                    <li className="flex items-center" data-id="element-1529">
                      <div className="w-24 bg-gray-200 h-4 rounded-full mr-3" data-id="element-1530">
                        <div className="bg-blue-700 h-4 rounded-full" style={{
                        width: '15%'
                      }} data-id="element-1531"></div>
                      </div>
                      <span className="text-sm" data-id="element-1532">Mathematics (15%)</span>
                    </li>
                    <li className="flex items-center" data-id="element-1533">
                      <div className="w-24 bg-gray-200 h-4 rounded-full mr-3" data-id="element-1534">
                        <div className="bg-blue-700 h-4 rounded-full" style={{
                        width: '15%'
                      }} data-id="element-1535"></div>
                      </div>
                      <span className="text-sm" data-id="element-1536">Social Sciences (15%)</span>
                    </li>
                    <li className="flex items-center" data-id="element-1537">
                      <div className="w-24 bg-gray-200 h-4 rounded-full mr-3" data-id="element-1538">
                        <div className="bg-blue-700 h-4 rounded-full" style={{
                        width: '10%'
                      }} data-id="element-1539"></div>
                      </div>
                      <span className="text-sm" data-id="element-1540">Business Studies (10%)</span>
                    </li>
                    <li className="flex items-center" data-id="element-1541">
                      <div className="w-24 bg-gray-200 h-4 rounded-full mr-3" data-id="element-1542">
                        <div className="bg-blue-700 h-4 rounded-full" style={{
                        width: '15%'
                      }} data-id="element-1543"></div>
                      </div>
                      <span className="text-sm" data-id="element-1544">Others (15%)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="text-center" data-id="element-1545">
              <Button href="/academics/faculty" variant="outline" data-id="element-1546">
                Meet Our Full Teaching Staff
              </Button>
            </div>
          </div>
        </section>
        {/* Academic Calendar Section */}
        <section className="mb-16" id="calendar" data-id="element-1547">
          <div className="text-center mb-10" data-id="element-1548">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1549">
              Academic Calendar
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1550"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1551">
              Important dates and events for the 2023/2024 academic year.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1552">
            <div className="bg-blue-900 text-white p-4" data-id="element-1553">
              <h3 className="text-xl font-bold" data-id="element-1554">2023/2024 Academic Calendar</h3>
            </div>
            <div className="p-6" data-id="element-1555">
              <Accordion variant="bordered" data-id="element-1556">
                <AccordionItem title="First Term (September - December 2023)" defaultOpen data-id="element-1557">
                  <div className="overflow-x-auto" data-id="element-1558">
                    <table className="min-w-full bg-white" data-id="element-1559">
                      <thead data-id="element-1560">
                        <tr className="bg-blue-100" data-id="element-1561">
                          <th className="py-2 px-4 text-left" data-id="element-1562">Date</th>
                          <th className="py-2 px-4 text-left" data-id="element-1563">Event</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-1564">
                        <tr data-id="element-1565">
                          <td className="py-2 px-4 font-medium" data-id="element-1566">
                            September 10, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1567">
                            Boarding Students Return
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1568">
                          <td className="py-2 px-4 font-medium" data-id="element-1569">
                            September 11, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1570">
                            First Term Begins / Classes Resume
                          </td>
                        </tr>
                        <tr data-id="element-1571">
                          <td className="py-2 px-4 font-medium" data-id="element-1572">
                            October 1, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1573">
                            Independence Day (Public Holiday)
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1574">
                          <td className="py-2 px-4 font-medium" data-id="element-1575">
                            October 16-20, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1576">Mid-Term Examinations</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1577">
                          <td className="py-2 px-4 font-medium" data-id="element-1578">
                            October 21-24, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1579">Mid-Term Break</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1580">
                          <td className="py-2 px-4 font-medium" data-id="element-1581">
                            November 10, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1582">
                            Inter-House Sports Competition
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1583">
                          <td className="py-2 px-4 font-medium" data-id="element-1584">
                            December 4-15, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1585">First Term Examinations</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1586">
                          <td className="py-2 px-4 font-medium" data-id="element-1587">
                            December 15, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1588">
                            End of First Term / Christmas Carol
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1589">
                          <td className="py-2 px-4 font-medium" data-id="element-1590">
                            December 16, 2023
                          </td>
                          <td className="py-2 px-4" data-id="element-1591">Students Vacation</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </AccordionItem>
                <AccordionItem title="Second Term (January - April 2024)" data-id="element-1592">
                  <div className="overflow-x-auto" data-id="element-1593">
                    <table className="min-w-full bg-white" data-id="element-1594">
                      <thead data-id="element-1595">
                        <tr className="bg-blue-100" data-id="element-1596">
                          <th className="py-2 px-4 text-left" data-id="element-1597">Date</th>
                          <th className="py-2 px-4 text-left" data-id="element-1598">Event</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-1599">
                        <tr data-id="element-1600">
                          <td className="py-2 px-4 font-medium" data-id="element-1601">
                            January 7, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1602">
                            Boarding Students Return
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1603">
                          <td className="py-2 px-4 font-medium" data-id="element-1604">
                            January 8, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1605">
                            Second Term Begins / Classes Resume
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1606">
                          <td className="py-2 px-4 font-medium" data-id="element-1607">
                            February 15, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1608">
                            Founders' Day Celebration
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1609">
                          <td className="py-2 px-4 font-medium" data-id="element-1610">
                            February 19-23, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1611">Mid-Term Examinations</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1612">
                          <td className="py-2 px-4 font-medium" data-id="element-1613">
                            February 24-27, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1614">Mid-Term Break</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1615">
                          <td className="py-2 px-4 font-medium" data-id="element-1616">
                            March 15, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1617">Career Day</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1618">
                          <td className="py-2 px-4 font-medium" data-id="element-1619">
                            April 1-12, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1620">
                            Second Term Examinations
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1621">
                          <td className="py-2 px-4 font-medium" data-id="element-1622">
                            April 12, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1623">End of Second Term</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1624">
                          <td className="py-2 px-4 font-medium" data-id="element-1625">
                            April 13, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1626">Students Vacation</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </AccordionItem>
                <AccordionItem title="Third Term (April - July 2024)" data-id="element-1627">
                  <div className="overflow-x-auto" data-id="element-1628">
                    <table className="min-w-full bg-white" data-id="element-1629">
                      <thead data-id="element-1630">
                        <tr className="bg-blue-100" data-id="element-1631">
                          <th className="py-2 px-4 text-left" data-id="element-1632">Date</th>
                          <th className="py-2 px-4 text-left" data-id="element-1633">Event</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200" data-id="element-1634">
                        <tr className="bg-gray-50" data-id="element-1635">
                          <td className="py-2 px-4 font-medium" data-id="element-1636">
                            April 28, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1637">
                            Boarding Students Return
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1638">
                          <td className="py-2 px-4 font-medium" data-id="element-1639">
                            April 29, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1640">
                            Third Term Begins / Classes Resume
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1641">
                          <td className="py-2 px-4 font-medium" data-id="element-1642">May 1, 2024</td>
                          <td className="py-2 px-4" data-id="element-1643">
                            Workers' Day (Public Holiday)
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1644">
                          <td className="py-2 px-4 font-medium" data-id="element-1645">
                            May 20-24, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1646">
                            WAEC/NECO Examinations Begin (SS3)
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1647">
                          <td className="py-2 px-4 font-medium" data-id="element-1648">
                            June 3-7, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1649">Mid-Term Examinations</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1650">
                          <td className="py-2 px-4 font-medium" data-id="element-1651">
                            June 8-11, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1652">Mid-Term Break</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1653">
                          <td className="py-2 px-4 font-medium" data-id="element-1654">
                            July 1-12, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1655">Third Term Examinations</td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1656">
                          <td className="py-2 px-4 font-medium" data-id="element-1657">
                            July 19, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1658">
                            Speech & Prize-Giving Day
                          </td>
                        </tr>
                        <tr className="bg-gray-50" data-id="element-1659">
                          <td className="py-2 px-4 font-medium" data-id="element-1660">
                            July 20, 2024
                          </td>
                          <td className="py-2 px-4" data-id="element-1661">
                            End of Academic Year / Students Vacation
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </AccordionItem>
              </Accordion>
              <div className="mt-6 bg-yellow-50 p-4 rounded-lg border border-yellow-100" data-id="element-1662">
                <div className="flex items-start" data-id="element-1663">
                  <CalendarIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" data-id="element-1664" />
                  <div data-id="element-1665">
                    <p className="font-bold text-blue-900" data-id="element-1666">Important Note:</p>
                    <p className="text-gray-700 text-sm" data-id="element-1667">
                      Dates are subject to change. Parents and students will be
                      notified of any adjustments to the academic calendar
                      through official communication channels.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Clubs & Societies Section */}
        <section id="clubs" data-id="element-1668">
          <div className="text-center mb-10" data-id="element-1669">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-1670">
              Clubs & Societies
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-1671"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-1672">
              Explore the various extracurricular activities that enhance our
              academic program and develop students' talents and interests.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1673">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow" data-id="element-1674">
              <div className="flex items-center mb-4" data-id="element-1675">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-1676">
                  <BeakerIcon className="h-6 w-6 text-blue-700" data-id="element-1677" />
                </div>
                <h3 className="text-lg font-bold text-blue-900" data-id="element-1678">
                  Science Club
                </h3>
              </div>
              <p className="text-gray-700 mb-3" data-id="element-1679">
                For students passionate about science and technology. Activities
                include experiments, robotics, coding, and preparation for
                science competitions.
              </p>
              <div className="text-sm text-gray-600" data-id="element-1680">
                <span className="font-medium" data-id="element-1681">Meeting Day:</span> Tuesdays
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow" data-id="element-1682">
              <div className="flex items-center mb-4" data-id="element-1683">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-1684">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1685">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 001 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" data-id="element-1686" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-blue-900" data-id="element-1687">
                  Debate Society
                </h3>
              </div>
              <p className="text-gray-700 mb-3" data-id="element-1688">
                Focuses on developing public speaking, critical thinking, and
                argumentation skills. Members participate in debates, public
                speaking contests, and model UN conferences.
              </p>
              <div className="text-sm text-gray-600" data-id="element-1689">
                <span className="font-medium" data-id="element-1690">Meeting Day:</span> Wednesdays
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow" data-id="element-1691">
              <div className="flex items-center mb-4" data-id="element-1692">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-1693">
                  <PenToolIcon className="h-6 w-6 text-blue-700" data-id="element-1694" />
                </div>
                <h3 className="text-lg font-bold text-blue-900" data-id="element-1695">
                  Art & Photography Club
                </h3>
              </div>
              <p className="text-gray-700 mb-3" data-id="element-1696">
                For creative students interested in visual arts. Activities
                include drawing, painting, sculpture, photography, and
                organizing art exhibitions.
              </p>
              <div className="text-sm text-gray-600" data-id="element-1697">
                <span className="font-medium" data-id="element-1698">Meeting Day:</span> Thursdays
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow" data-id="element-1699">
              <div className="flex items-center mb-4" data-id="element-1700">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-1701">
                  <MusicIcon className="h-6 w-6 text-blue-700" data-id="element-1702" />
                </div>
                <h3 className="text-lg font-bold text-blue-900" data-id="element-1703">
                  Music & Drama Club
                </h3>
              </div>
              <p className="text-gray-700 mb-3" data-id="element-1704">
                For students interested in performing arts. Members learn to
                play instruments, sing, act, and participate in school
                productions and competitions.
              </p>
              <div className="text-sm text-gray-600" data-id="element-1705">
                <span className="font-medium" data-id="element-1706">Meeting Day:</span> Fridays
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow" data-id="element-1707">
              <div className="flex items-center mb-4" data-id="element-1708">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-1709">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1710">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" data-id="element-1711" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-blue-900" data-id="element-1712">
                  Environmental Club
                </h3>
              </div>
              <p className="text-gray-700 mb-3" data-id="element-1713">
                Focuses on environmental conservation and sustainability.
                Activities include tree planting, recycling initiatives, and
                awareness campaigns.
              </p>
              <div className="text-sm text-gray-600" data-id="element-1714">
                <span className="font-medium" data-id="element-1715">Meeting Day:</span> Mondays
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow" data-id="element-1716">
              <div className="flex items-center mb-4" data-id="element-1717">
                <div className="p-3 bg-blue-100 rounded-full mr-3" data-id="element-1718">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1719">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 001 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" data-id="element-1720" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-blue-900" data-id="element-1721">
                  Robotics Club
                </h3>
              </div>
              <p className="text-gray-700 mb-3" data-id="element-1722">
                For students interested in robotics and engineering. Members
                learn to design, build, and program robots, and participate in
                robotics competitions.
              </p>
              <div className="text-sm text-gray-600" data-id="element-1723">
                <span className="font-medium" data-id="element-1724">Meeting Day:</span> Tuesdays
              </div>
            </div>
          </div>
          <div className="mt-8 text-center" data-id="element-1725">
            <Button href="/student-life/clubs" variant="primary" className="mx-auto" data-id="element-1726">
              Explore All Clubs & Societies
            </Button>
          </div>
        </section>
      </div>
    </div>;
};
export default Academics;